/**
 * Application root: providers, then routes.
 *
 * Provider order matters. The theme wraps everything so the error boundary can
 * render styled; the query client sits inside it so any component can fetch;
 * the router is innermost because routes are what consume both.
 */
import { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { loadToken } from '@/lib/canvas/tokenStore';
import { enableCachePersistence } from '@/lib/persist';
import { queryClient } from '@/lib/queryClient';
import { startWriteQueue } from '@/lib/writeQueue';
import { ThemeProvider } from '@/theme/ThemeProvider';

import { AppRoutes } from './routes';

export function App() {
  // The token lives in IndexedDB, which is async, while the request path is
  // synchronous. Loading it once here fills the in-memory mirror before any
  // query fires, so the first request is not a spurious "no token" error.
  const [tokenLoaded, setTokenLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void loadToken().finally(() => {
      if (!cancelled) setTokenLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Cache persistence and the write queue are attached to the singleton client
  // here rather than inside `queryClient.ts`, so that importing the module in a
  // test does not start touching IndexedDB or adding window listeners.
  useEffect(() => {
    const stopPersisting = enableCachePersistence(queryClient);
    const stopQueue = startWriteQueue((result) => {
      if (result.succeeded > 0) {
        // A write that landed changes what Canvas thinks; refetch rather than
        // patch the cache (brainstorm.md 1.1 — Canvas always wins).
        void queryClient.invalidateQueries();
      }
    });
    return () => {
      stopPersisting();
      stopQueue();
    };
  }, []);

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          {/* Opt into the v7 behaviours now: it silences the upgrade warnings
              and means a React Router bump later is not a behaviour change. */}
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            {tokenLoaded ? <AppRoutes /> : null}
          </BrowserRouter>
        </QueryClientProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}
